import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import { t, Trans } from "@lingui/macro"

import { Layout, Tag, Col, Row, Avatar, Button, Skeleton, Flex, Input, Tooltip } from "antd";
import { Spinner, CardFooter, Button as ButtonNew, UncontrolledDropdown, DropdownToggle, DropdownItem, DropdownMenu } from "reactstrap";

import { useNavigate } from 'react-router-dom';
import { sortBy, debounce } from 'lodash';

import dayjs from "dayjs";
import "dayjs/locale/en";

import { filterFormPropertySchema, Color } from './util'
import { FilterForm } from "../FormComponent";

import UWEEntityApi from "../../api/UWEEntityApi";
import useSearchParams from "../../util/useSearchParams";
import Pagination from "../Pagination";
import useResourceLoader from "../../util/useResourceLoader";
import Loader from "../Loader";
import leftPad from "../../util/leftPad";
import SearchBar from "../Search/SearchBar";

import { AuthContext } from "../../context/AuthContext";
import parseFormDefinition from "../../util/parseFormDefinition";

const avatarStyles = {
  completed: {
    backgroundColor: '#28a745',
    color: 'white',
  },
  inProgress: {
    backgroundColor: '#f9b943',
    color: 'black',
  },
  confirmed: {
    backgroundColor: `#4aa0ea`,
    color: 'white',
  },
  autoClockout: {
    backgroundColor: '#ba0606',
    color: 'white',
  },
  pending: {
    backgroundColor: '#949494',
    color: 'white',
  },
  missed: {
    backgroundColor: '#ff1111',
    color: 'white',
  },
}

const getShiftInstanceId = (instance) => {
  const id = instance.id;
  const shiftDate = instance.metadata.shiftDate;
  const year = dayjs(shiftDate).format('YY');
  return `${year}-${id}`;
}

function ShiftViewer({ element }) {
  const ref = useRef();
  // Get props
  const {
    entityType,
    startDate,
    endDate,
    status,
  } = element;
  const [reportEnabled, setReportEnabled] = useState(true);
  const [secondReportEnabled, setSecondReportEnabled] = useState(true);
  

  // Routing
  const navigate = useNavigate();

  const auth = useContext(AuthContext);

  const formContext = { auth };

  const [formDefinition, sFilters] = parseSearchFormDefinition(element);

  // Get hashmap from status to color:
  // Jammy: Esto no es necesario solo se tiene que validar si status esta vacio o no al momento de usarlo.
  //        basicamente estas cogiendo status y lo estas pasando a un objeto nuevo y devolviendo el objeto
  const statusColors = useMemo(() => {
    const map = {};
    status.forEach(({name, color}) => {
      map[name] = color;
    });
    return map;
  }, [status]);

  // Get default start and end dates
  const defaultStartDate =
    startDate ||
    dayjs()
      .format("YYYY-MM-DD");
  const defaultEndDate =
    endDate ||
    dayjs()
      .add(1, "week")
      .format("YYYY-MM-DD");

  // Get entity name
  const entityName = useMemo(() => {
    return Array.isArray(entityType)? entityType.join(","): entityType;
  }, [entityType]);

  // ============================
  // = Filtering & Pagination
  // ============================

  // Shifts (no pagination yet)
  // const size = 2500; // Set more sensible default later...
  // const offset = 0;
  const [shifts, setShifts] = useState([]);

  const [
    [offset, setOffset],
    [size, setSize],
    [sortBy, setSortBy]
  ] = useSearchParams({
    offset: 0,
    size: 50,
    sortBy: element.defaultFilter
  });

  const [isLoadingShifts, setIsLoadingShifts] = useState(false);

  // Get the starting values for the filters
  const [filterValues, setFilterValues] = useState({
    "metadata-date-greaterthanorequal-shiftDate": defaultStartDate,
    "metadata-date-lessthanorequal-shiftDate": defaultEndDate
  });

  // --------------------
  // - Fetch shift data
  // --------------------

  const addFiltersToPayload = (payload) => {
    if (!filterValues) {
      return;
    }
    Object.entries(filterValues).forEach(([key, value]) => {
      payload[key] = value;
    });
  };

  // Fetch the shift data from UWE
  const fetchShiftData = async () => {
    if (!filterValues) {
      return [];
    }
    const resource = entityName
      ? `uwe-entities/${entityName}/list`
      : null;
    if (!resource) {
      return [];
    }

    const payload = {
      resource,
      offset,
      size,
      sortBy: element.defaultFilter,
      "tray": window.location.href.split("/")[4],
    };

    addFiltersToPayload(payload);

    const data = await UWEEntityApi.getWorkTray(payload);
    return data;
  };


  function mergeParamsIntoFilters(filters, params) {
    if (!params || typeof params !== "object") {
      return filters;
    }

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        filters[key] = value
          .filter(Boolean)
          .map(String)
          .join(",");
      } else if (value != null && typeof value !== "object") {
        filters[key] = String(value);
      }
    });

    return filters;
  }


  const [paging, loading, error] = useResourceLoader(async () => {
    const resource = entityName
      ? `uwe-entities/${entityName}/list`
      : null;
    if (!resource) {
      return [];
    }

    const payload = {
      resource,
      offset,
      size,
      sortBy: element.defaultFilter,
      "tray": window.location.href.split("/")[4],
    };

    addFiltersToPayload(payload);
    
    const data = await UWEEntityApi.getWorkTray(payload);
    setShifts(data.items);

    setIsLoadingShifts(false);
    return Array.isArray(data)
      ? {
          from: 0,
          to: data.length,
          count: data.length,
          items: data
        }
      : data;
  }, [size, offset, sortBy, element, filterValues]);


  const { from = 0, to = 0, count = 0, items: data = [] } = paging || {};

  // useEffect(() => {
  //   const bootstrapAsync = async () => {
  //     setIsLoadingShifts(true);

  //     const { from, to, count, items } = await fetchShiftData();
  //     setShifts(items);

  //     setIsLoadingShifts(false);
  //   };

  //   // Only show shifts once all filterValues are obtained
  //     bootstrapAsync();
  // }, [offset, filterValues]);
  
  // --------------------------
  // - Renders the page
  // --------------------------

  const [searchTerm, setSearchTerm] = useState('');
  const [currentStatus, setCurrentStatus] = useState(null);

  const onSearch = debounce(v => setSearchTerm(v.toLowerCase()), 350, { trailing: true })

  function formatTimeByRegion(dateTime, region) {
    if (!dateTime) return "---";
    const timezone = region === "Windmar Puerto Rico" ? "America/Puerto_Rico" : "America/New_York";
    const utcDate = dateTime.includes('T') ? new Date(dateTime) : new Date(`${dateTime} UTC`);
    return utcDate.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  function calculateShiftHours(allocation) {
    let totalMinutes = 0;
    
    const parseDateTime = (dateTime) => {
      if (!dateTime) return null;
      return dateTime.includes('T') ? new Date(dateTime) : new Date(`${dateTime} UTC`);
    };
    
    // Calculate first shift duration
    if (allocation?.clockIn) {
      const clockOut = allocation?.clockOut || allocation?.adminClockOut;
      if (clockOut) {
        const start = parseDateTime(allocation.clockIn);
        const end = parseDateTime(clockOut);
        if (start && end) {
          totalMinutes += (end - start) / (1000 * 60);
        }
      }
    }
    
    // Calculate second shift duration if available
    if (allocation?.secondClockIn) {
      // Only use adminClockOut if no regular clockOut was used for first shift
      const secondClockOut = allocation?.secondClockOut || 
        (!allocation?.clockOut ? allocation?.adminClockOut : null);
      if (secondClockOut) {
        const start = parseDateTime(allocation.secondClockIn);
        const end = parseDateTime(secondClockOut);
        if (start && end) {
          totalMinutes += (end - start) / (1000 * 60);
        }
      }
    }
    
    if (totalMinutes <= 0) return "---";
    
    let hours = Math.floor(totalMinutes / 60);
    let minutes = Math.round(totalMinutes % 60);
    
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  function extractCommonFields(item) {
    return {
      "Shift ID": item.id,
      "Shift Name": item.metadata?.name ?? "---",
      "Location": item.metadata?.locationName ?? "---",
      "Shift Type": item.metadata?.jobTypeTitle ?? "---",
      "Shift Status": item.metadata?.jobStatus ?? "---",
      "Date": item.metadata?.shiftDate ?? "---",
      "Start Time": item.metadata?.startTime ? item.metadata.startTime.slice(0, 5) : "---",
      "End Time": item.metadata?.endTime ? item.metadata.endTime.slice(0, 5) : "---",
      "Region": item.metadata?.region ?? "---"
    };
  }


  const exportTray = async (e) => {
      e.preventDefault();
      if (!reportEnabled) {
          return;
      }
      setReportEnabled(false);
      const resource = entityName ? `uwe-entities/${entityName}/list` : null;
      if (!resource) return null;

      const payload = {
        resource,
        offset: 0,
        size: 200000,
        sortBy: element.defaultFilter,
        "tray": window.location.href.split("/")[4],
      };

      addFiltersToPayload(payload);
      
      const data = await UWEEntityApi.getWorkTray(payload);

      let exportDataArray = [];
      
      exportDataArray = data.items.map(item => {
        let allocations = [];
      
        try {
          allocations = JSON.parse(item.metadata.shiftAllocations);
        } catch (e) {
          allocations = [];
        }
      
        // If no allocations, still return one row with "---" values for those fields
        if (!Array.isArray(allocations) || allocations.length === 0) {
          return {
            "Name": "---",
            "Email": "---",
            ...extractCommonFields(item),
            "User Id": "---",
            "Status": "---",
            "Clock In": "---",
            "Admin Clock In": "---",
            "Clock Out": "---",
            "Admin Clock Out": "---",
            "Second Clock In": "---",
            "Admin Second Clock In": "---",
            "Second Clock Out": "---",
            "Admin Second Clock Out": "---",
            "Admin Clock Out Time": "---",
            "Auto clocked-out": "---",
            "Shift Hours": "---",
            "Reason For Leaving": "---"
          };
        }
      
        // For each allocation, create a row with extracted fields
        return allocations.map(allocation => ({
          "Name": allocation?.userId?.name ?? "---",
          "Email": allocation?.userId?.email ?? "---",
          ...extractCommonFields(item),
          "User Id": allocation?.userId?.id ?? "---",
          "Status": allocation?.status ?? "---",
          "Clock In": formatTimeByRegion(allocation?.clockIn, item.metadata?.region),
          "Admin Clock In": allocation?.adminClockInFlag === true ? "Yes" : "No",
          "Clock Out": formatTimeByRegion(allocation?.clockOut, item.metadata?.region),
          "Admin Clock Out": allocation?.adminClockOutFlag === true ? "Yes" : "No",
          "Second Clock In": formatTimeByRegion(allocation?.secondClockIn, item.metadata?.region),
          "Admin Second Clock In": allocation?.adminSecondClockInFlag === true ? "Yes" : "No",
          "Second Clock Out": formatTimeByRegion(allocation?.secondClockOut, item.metadata?.region),
          "Admin Second Clock Out": allocation?.adminSecondClockOutFlag === true ? "Yes" : "No",
          "Admin Clock Out Time": formatTimeByRegion(allocation?.adminClockOut, item.metadata?.region),
          "Auto clocked-out": allocation?.autoClockout === true ? "Yes" : "No" ?? "---",
          "Shift Hours": calculateShiftHours(allocation),
          "Reason For Leaving": allocation?.reasonForLeaving ?? "---",
        }));
      }).flat();
      
      
      if (Array.isArray(exportDataArray)) {
          const csvStr = JSONtoCSV(exportDataArray);
          downloadCSV(csvStr);
      }
      setReportEnabled(true);
  }

  const exportSecondTray = async (e) => {
      e.preventDefault();
      if (!secondReportEnabled) {
          return;
      }
      setSecondReportEnabled(false);
      const resource = entityName ? `uwe-entities/${entityName}/list` : null;
      if (!resource) return null;

      const payload = {
        resource,
        offset: 0,
        size: 200000,
        sortBy: element.defaultFilter,
        "tray": window.location.href.split("/")[4],
      };

      addFiltersToPayload(payload);
      
      const data = await UWEEntityApi.getWorkTray(payload);

      let exportDataArray = [];
      
      exportDataArray = data.items.map(item => {
        let allocations = [];
      
        try {
          allocations = JSON.parse(item.metadata.shiftAllocations);
        } catch (e) {
          allocations = [];
        }
        const totalSlots = Number(item.metadata.availableSlots);

        if (!Array.isArray(allocations) || allocations.length === 0 || !Number.isInteger(totalSlots)) {
          return {
            ...extractCommonFields(item),
            "Total Slots": totalSlots ?? "---",
            "Available": totalSlots ?? "---",
            "Confirmed": "0",
          };
        }
        return {
            ...extractCommonFields(item),
            "Total Slots": totalSlots ?? "---",
            "Available": totalSlots - allocations.length,
            "Confirmed": allocations.length,
          };
      }).flat();
      
      
      if (Array.isArray(exportDataArray)) {
          const csvStr = JSONtoCSV(exportDataArray);
          downloadCSV(csvStr, `Shift_Report`);
      }
      setSecondReportEnabled(true);
  }

  function JSONtoCSV(jsonArray, headers) {
      if (jsonArray === null || jsonArray === undefined) { return null };
      const replacer = (key, value) => value === null ? '' : value
      const header = Object.keys(jsonArray[0])
      let csv = jsonArray.map(row => header.map(fieldName =>
          JSON.stringify(row[fieldName], replacer)).join(','))
      csv.unshift(header.join(','))
      csv = csv.join('\r\n')
      return csv;
  }

  function downloadCSV(csvStr, title = null) {

      const hiddenElement = document.createElement('a');
      const universalBOM = "\uFEFF";
      const blobData = new Blob([universalBOM + csvStr], { type: 'text/csv' });
      const blobURL = URL.createObjectURL(blobData);
      hiddenElement.href = blobURL;
      hiddenElement.target = '_blank';
      hiddenElement.download = title?`${title}.csv`:`report.csv`;
      hiddenElement.click();
  }



  const renderShiftAvatars = (shiftData) => {
    const { availableSlots, shiftAllocations, shiftDate, endTime } = shiftData.metadata;
    const id = shiftData.id;

    const shiftEnd = dayjs(`${shiftDate} ${endTime}`)
    const hasShiftEnded = dayjs().isAfter(shiftEnd)

    const allocations = shiftAllocations ? JSON.parse(shiftAllocations) : [];
    const availableUserSlots = Math.max(parseInt(availableSlots, 10) || 0, 0);

    const avatars = [];
    for (let i = 0; i < availableUserSlots; i += 1) {
      if (i < allocations.length) {
        const alloc = allocations[i]
        const name = alloc?.userId?.name || "";
        const initials = name.split(' ').map((t) => t[0]).join('').toUpperCase();
        const avatarId = `avatar-${id}-${i}`;

        let status = alloc?.status;
        let style = avatarStyles.pending;
        if (alloc?.status === 'Completed') {
          if (alloc?.autoClockout) {
            style = avatarStyles.autoClockout;
            status = alloc?.outOfBounds ? 'Completed (ACO Geo Location)' : 'Completed (ACO)';
          } else {
            style = avatarStyles.completed
          }
        } else if (alloc?.status === 'In Progress') {
          style = avatarStyles.inProgress;
        } else if (alloc?.status === 'Confirmed') {
          if (hasShiftEnded) {
            style = avatarStyles.missed;
            status = `Missed`
          } else {
            style = avatarStyles.confirmed;
          }
        }

        avatars.push(
          <Tooltip key={`avatar-${id}-${i}`} title={`${name} (${status})`} placement="top">
             <Avatar size="large" id={avatarId} style={style}>{initials}</Avatar>
          </Tooltip>
        );
      } else {
        // Placeholder Avatar for unassigned slot
        avatars.push(
          <Skeleton.Avatar
            key={`avatar-${id}-${i}`}
            shape="circle"
            size="large"
            style={{
              borderStyle: "dashed",
              borderColor: "#D3D3D3",
              borderWidth: "1px",
              position: "relative",
              verticalAlign: "middle",
            }}
          />
        );
      }
    }

    return (
      <Avatar.Group
        key={`avatar-group-${id}`}
        size="large"
        max={{
          count: 6,
        }}
      >
        {avatars}
      </Avatar.Group>
    );
  };

  const renderShiftCard = (shiftData) => {
    const { jobTypeTitle, locationName, shiftDate, startTime, endTime, jobStatus, name } = shiftData.metadata;
    const id = shiftData.id;

    const formattedStartTime = dayjs(`${shiftDate} ${startTime}`).format('h:mm a');
    const formattedEndTime = dayjs(`${shiftDate} ${endTime}`).format('h:mm a');
    const formattedShiftDate = dayjs(shiftDate).format('dddd MMMM D, YYYY')

    const shiftCardId = getShiftInstanceId(shiftData);

    return (
      <button
        key={`shift-card-${id}`}
        onClick={() => window.open(`/workflow/shift-instance/${id}`, "_blank")}
        style={{
          background: "transparent",
          border: "none",
          outline: "none",
          display: "block",
          padding: 0,
          width: "100%",
          cursor: "pointer",
          marginTop: 2,
          marginBottom: 15
        }}
      >
        <div
          key={id}
          className="shift-viewer-card"
          style={{borderRadius: 8, padding: 12}}
        >
          <Row>
            <Col xs={8}>
              <Flex
                style={{height: "100%"}}
                vertical
                align="flex-start"
                justify="space-between"
              >
                <span><b>{name}</b></span>
                <span>{locationName}</span>
                <span><em>{shiftCardId}</em></span>
              </Flex>
            </Col>
            <Col xs={8}>
              <Flex
                style={{height: "100%"}}
                vertical
                alignItems="center"
              >
                <div>{formattedShiftDate}</div>
                <p>{formattedStartTime} - {formattedEndTime}</p>
                <div>{jobTypeTitle}</div>
              </Flex>
            </Col>
            <Col xs={8}>
              <Flex
                style={{height: "100%"}}
                vertical
                align="flex-end"
                justify="space-between"
              >
                <Tag 
                  style={{ 
                    fontSize: 16,
                    color: Color.getContrastTextColor(statusColors[jobStatus]),
                    padding: 6,
                    marginBottom: 8,
                    marginRight: 0,
                  }}
                  color={statusColors[jobStatus]}
                >
                  {jobStatus}
                </Tag>
                {renderShiftAvatars(shiftData)}
              </Flex>
            </Col>
          </Row>
        </div>
      </button>
    )
  };

  const renderShiftCards = () => {
    if(loading) {
      return (
        <div style={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: '80px' }}>
          <Spinner size="lg" />
        </div>
      );
    }
    if(!shifts){
      return <p>No shifts found.</p>;
    }

    let allShifts = shifts?.sort((s1, s2) => {
      const date1 = dayjs(s1.metadata.shiftDate);
      const date2 = dayjs(s2.metadata.shiftDate);
      if (date1.isBefore(date2)) return -1;
      else if (date1.isAfter(date2)) return 1;
      else return 0;
    });

    if (searchTerm) {
      allShifts = allShifts?.filter((sh) => {
        let doesMatch = false;
        if (sh.metadata.name) {
          doesMatch |= sh.metadata.name.toLowerCase().includes(searchTerm);
        }
        if (sh.metadata.locationName) {
          doesMatch |= sh.metadata.locationName.toLowerCase().includes(searchTerm);
        }
        if (sh.metadata.shiftDate) {
          const shiftCardId = getShiftInstanceId(sh);
          doesMatch |= shiftCardId.includes(searchTerm);
        }
        return doesMatch;
      });
    }

    if(allShifts.length === 0){
      return <p>No shifts found.</p>;
    }

    return allShifts?.map(renderShiftCard);
  };

  const handleSearch = update => {
    setFilterValues({
      ...filterValues,
      ...update.formData
    });
  };

  return (
    <Flex className="worktray" vertical justify="flex-start">
      <Row justify="space-between">
        <Col>
          <h2 className={`title ${element.tableActions ? 'has-buttons' : ''}`}>
            <Trans>All Shifts</Trans>
          </h2>
        </Col>
        <Col>
          <div className="float-right">
            <UncontrolledDropdown>
              <DropdownToggle caret className="shift-viewer-export-btn"><Trans>Export Reports</Trans></DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={exportTray}>
                {
                    reportEnabled ? (
                        <div><Trans>Summary Report of Clock-In and Clock-Out</Trans></div>
                    ) : (
                        <div><Loader centered sm><Trans>Exporting...</Trans></Loader></div>
                    )
                }
                </DropdownItem>
                <DropdownItem onClick={exportSecondTray}>
                {
                    secondReportEnabled ? (
                        <div><Trans>Summary Report of Shifts</Trans></div>
                    ) : (
                        <div><Loader centered sm><Trans>Exporting...</Trans></Loader></div>
                    )
                }
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </Col>
      </Row>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          marginTop: 16
        }}
      >
        <small style={{ color: "gray", fontSize: "10pt" }}>
          <Trans>Filters:</Trans>
        </small>
        <FilterForm
          className="worktray-filters"
          tagName="div"
          ref={ref}
          formData={filterValues}
          formContext={{...formContext, ...filterValues}}
          {...formDefinition}
          onChange={handleSearch}
        />
      </div>
      <Row style={{ marginTop: 16, flexGrow: 1, minHeight: 0 }}>
        <Col
          span={4}
          style={{
            borderStyle: 'solid',
            borderColor: '#D3D3D3',
            borderWidth: "0px 1px 0px 0px",
            maxHeight: '100%',
            overflow: 'auto',
          }}
        >
          <Flex
            vertical
            align="flex-start"
            justify="center"
            style={{
              borderRight: 10,
              borderRightColor: 'black',
            }}
          >
            <h4><Trans>Status</Trans></h4>
            {
              status.map(({name, color}) => (
                <Button
                  key={name}
                  style={{
                    width: "13vw", 
                    minWidth: "50px",
                    height: "60px",
                    textAlign: 'flex-start',
                    justify: 'flex-start',
                    whiteSpace: 'normal',

                    color: Color.getContrastTextColor(color),
                    backgroundColor: color, 
                    borderColor: name === currentStatus ? Color.getContrastTextColor(color) : null,
    
                    // To customize antd card color
                    margin: "4px",
                    borderRadius: "12px",
                  }}
                  onClick={() => {
                    const val = name === currentStatus ? null : name;
                    setCurrentStatus(val);
                    setFilterValues({...filterValues, 'metadata-like-jobStatus': val})
                  }}
                > 
                    {name}
                </Button>
              ))
            }
          </Flex>
        </Col>
        <Col
          span={20}
          style={{ paddingLeft: '2vw', height: '100%' }}
        >
          <Flex style={{ flexDirection: "column", height: "100%" }}>
            <Flex align="center" gap={8}>
              <h4><Trans>Shifts</Trans></h4>
              {isLoadingShifts && <Spinner size="sm" style={{ marginBottom: 8 }} />}
            </Flex>
            <Flex>
              {/* <SearchBar
                placeholder={t`Search`}
                onChange={onSearch}
              /> */}
              {
                currentStatus &&
                <Tag
                  style={{
                    top: 4,
                    left: 10,
                    height: 23,
                    color: Color.getContrastTextColor(statusColors[currentStatus])
                  }}
                  color={statusColors[currentStatus]}
                  closable
                  closeIcon={<i className="fa fa-times-circle" aria-hidden="true"/>}
                  onClose={() => {
                    setFilterValues({...filterValues, 'metadata-like-jobStatus': null})
                    setCurrentStatus(null)
                  }}
                >
                  {currentStatus}
                </Tag>
              }
            </Flex>
            <div style={{ height: '100%', overflowY: 'scroll', marginRight: 10 }}>
              {renderShiftCards()}
            </div>
            <CardFooter className="pagination">
              <Pagination
                offset={from}
                count={count}
                size={size}
                setSize={setSize}
                setOffset={setOffset}
              />
            </CardFooter>
          </Flex>
        </Col>
      </Row>
    </Flex>
  );
}

ShiftViewer.rootSchema = {
  definitions: {
    mapFieldField: {
      type: ["string", "object"],
      "ui:field": "JsonField",
      showTree: true,
    },
  },
  properties: {
    entityType: {
      title: "Entity Type",
      type: "string",
      "map:field": "entityType",
      "ui:field": "LookupFormField",
      lookup: {
        resource: "Entity Types",
        api: "api:manage/objectschemas",
        params: { all: true },
        options: { useCache: true },
        jnx: "data.items",
        id: "name",
        label: "name",
      },
    },
    startDate: {
      title: "Start Date",
      type: "string",
      format: "date",
      structure: "Text",
      "map:field": "startDate",
    },
    endDate: {
      title: "End Date",
      type: "string",
      format: "date",
      structure: "Text",
      "map:field": "endDate",
    },
    defaultFilter: {
      title: "Default Filter",
      type: "string",
      "map:field": "defaultFilter"
    },

    status: {
      title: "Statuses",
      ":classNames": "from-col-1-size-2",
      type: "array",
      "map:array": "status",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            title: "Name",
            "map:field": "name",
          },
          color: {
            type: "string",
            title: "Color",
            "map:field": "color",
            structure: "Text",
            "ui:field": "ColorPicker",
          },
        },
      },
    },
    ...filterFormPropertySchema,
  },
};

const parseSearchFormDefinition = ({ filters }) => {
  if (filters) {
    const sFilters = {};
    const schema = filters.reduce((_, { key, ...obj }) => {
      _[key] = obj;
      return _;
    }, {});
    return [
      parseFormDefinition({
        schemaProps: {
          // ":classNames": "smallThreeRows",
        },
        schema
      }),
      sFilters
    ];
  }
  return [];
};

export default ShiftViewer;
